"use client"
import { useState, useEffect } from "react"
import { ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import WorkflowCanvas from "./components/WorkflowCanvas"
import HealthStatus from "./components/HealthStatus"
import ErrorBoundary from "./components/ErrorBoundary"
import SahayakSidebar from "./components/SahayakSidebar"
import GenerateContentPage from "./components/sahayak-pages/GenerateContentPage"
import LessonPlannerPage from "./components/sahayak-pages/LessonPlannerPage"
import UploadImagePage from "./components/sahayak-pages/UploadImagePage"
import StudentTrackerPage from "./components/sahayak-pages/StudentTrackerPage"
import MainDashboard from "./components/dashboard/MainDashboard"
import { Save, Play, HelpCircle, Menu } from "lucide-react"

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 1024)
  const [currentView, setCurrentView] = useState('dashboard')

  // Dynamic URL routing
  useEffect(() => {
    const path = window.location.hash.substring(1) || 'dashboard'
    if (path !== currentView) {
      setCurrentView(path)
    }
  }, [])

  // Handle window resize for responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setSidebarOpen(false)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen)
  }

  const handleNavigation = (item) => {
    setCurrentView(item.id)
    window.location.hash = item.id
    console.log(`Navigated to: dashboard/${item.id}`)
  }

  return (
    <div className="w-full h-screen bg-gray-50 flex overflow-hidden font-inter">
      {/* Sahayak Sidebar */}
      <SahayakSidebar 
        isOpen={sidebarOpen} 
        toggleSidebar={toggleSidebar}
        onNavigate={handleNavigation}
        currentView={currentView}
      />

      {/* Main Content */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ease-in-out ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-16'}`}>
        {/* Toolbar */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between shadow-sm">
          <div className="flex items-center space-x-4">
            <button
              onClick={toggleSidebar}
              className="p-2 hover:bg-gray-100 rounded-lg lg:hidden transition-colors"
            >
              <Menu className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex items-center space-x-3">
              <h1 className="text-xl font-semibold text-gray-900">Sahayak</h1>
              <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                {currentView.charAt(0).toUpperCase() + currentView.slice(1).replace('-', ' ')}
              </span>
            </div>
            <div className="hidden lg:flex items-center border-l border-gray-300 pl-4">
              <HealthStatus />
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {currentView === 'workflow' && (
              <>
                <button
                  onClick={() => window.sahayakFlow?.saveWorkflow()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 text-sm font-medium"
                  title="Save current workflow"
                >
                  <Save className="w-4 h-4" />
                  <span className="hidden sm:inline">Save Workflow</span>
                </button>
                <button
                  onClick={() => window.sahayakFlow?.runWorkflow()}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center space-x-2 text-sm font-medium"
                  title="Run the current workflow"
                >
                  <Play className="w-4 h-4" />
                  <span className="hidden sm:inline">Run Flow</span>
                </button>
              </>
            )}
            {currentView !== 'workflow' && (
              <button
                onClick={() => handleNavigation({ id: 'workflow', label: 'Workflow Editor' })}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2 text-sm font-medium"
                title="Switch to workflow editor"
              >
                <span>Workflow Editor</span>
              </button>
            )}
            <button
              onClick={() => {
                /* Add help/shortcuts modal */
              }}
              className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
              title="Help & shortcuts"
            >
              <HelpCircle className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-auto bg-gray-50">
          <ErrorBoundary>
            {currentView === 'dashboard' && <MainDashboard onNavigate={handleNavigation} />}
            {currentView === 'generate-content' && (
              <div className="max-w-7xl mx-auto">
                <GenerateContentPage />
              </div>
            )}
            {currentView === 'lesson-planner' && (
              <div className="max-w-7xl mx-auto">
                <LessonPlannerPage />
              </div>
            )}
            {currentView === 'upload-image' && (
              <div className="max-w-7xl mx-auto">
                <UploadImagePage />
              </div>
            )}
            {currentView === 'student-tracker' && (
              <div className="max-w-7xl mx-auto">
                <StudentTrackerPage />
              </div>
            )}
            {currentView === 'resource-library' && (
              <div className="max-w-4xl mx-auto p-8">
                <div className="text-center py-12">
                  <h1 className="text-3xl font-bold text-gray-900 mb-4">Resource Library</h1>
                  <p className="text-gray-600 text-lg">Educational resources and materials library</p>
                  <div className="mt-8 text-sm text-gray-500">Coming Soon</div>
                </div>
              </div>
            )}
            {currentView === 'settings' && (
              <div className="max-w-4xl mx-auto p-8">
                <div className="text-center py-12">
                  <h1 className="text-3xl font-bold text-gray-900 mb-4">Settings</h1>
                  <p className="text-gray-600 text-lg">Application settings and preferences</p>
                  <div className="mt-8 text-sm text-gray-500">Coming Soon</div>
                </div>
              </div>
            )}
            {currentView === 'workflow' && <WorkflowCanvas />}
          </ErrorBoundary>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={1500}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
        theme="colored"
        limit={3}
        className="toast-container"
      />
      
   
    </div>
  )
}

export default App
